import { useEffect, useState, useRef } from "react";
import { useAppSelector } from "hooks/redux";
import {
  Grid,
  TextField,
  FormControl,
  InputLabel,
  MenuItem,
  ListItemText,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Checkbox,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import { Controller } from "react-hook-form";
import { useLazyQuery, gql } from "@apollo/client";
import FieldError from "shared/organisms/dynamicform/FieldError";
import { StyledSelect } from "./styles";
import liveClassClientService from "utils/graphql/liveclassClient";
import { makeStyles } from "@mui/styles";
import { IMultiSelectFieldProps } from "./types";

const INITIAL_DATA_LIMIT = 20;
const SELECT_ALL_VALUE = "__select_all__";
const DUMMY_QUERY = gql`
  query DummyQuery {
    _dummy
  }
`;

const useStyles = makeStyles({
  border: {
    "& .css-m1opko-MuiFormLabel-root-MuiInputLabel-root": {
      background: "white",
      paddingRight: "5px",
      paddingLeft: "5px",
    },
  },
});

const MultiSelectWithSearch = ({
  field,
  control,
  errors,
  setValue,
  watch,
}: IMultiSelectFieldProps) => {
  const [options, setOptions] = useState<any[]>([]);
  const [initialOptions, setInitialOptions] = useState<any[]>([]);
  const [filteredOptions, setFilteredOptions] = useState<any[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);

  const selectedOptionsRef = useRef<any[]>(field.presetOptions ?? []);
  const debounceRef = useRef<any>(null);
  const prevParentValueRef = useRef<any>(undefined);

  const classes = useStyles();

  const parentValue = field.dependentOn
    ? watch?.(field.dependentOn)
    : null;

  const parentValueStr = JSON.stringify(parentValue);

  const isParentEmpty = Array.isArray(parentValue)
    ? parentValue.every((v: any) => !v)
    : !parentValue;

  const getDependentPayload = () => {
    if (!field.dependentOn || isParentEmpty) return {};
    
    if (field.formatDependentPayload) {
      return field.formatDependentPayload(parentValue);
    }

    if (Array.isArray(field.dependentOn)) {
      return field.dependentOn.reduce((acc: any, key: string, index: number) => {
        let val = parentValue[index];
        if (val === "true") val = true;
        if (val === "false") val = false;
        
        if (val !== undefined && val !== null && val !== "") {
          acc[key] = val;
        }
        return acc;
      }, {});
    }
    
    let singleVal = parentValue;
    if (singleVal === "true") singleVal = true;
    if (singleVal === "false") singleVal = false;
    
    return { [field.dependentOn]: singleVal };
  };

  // Read from Redux metadata when field.list is provided
  const metaData = useAppSelector((state: any) => state.metadataReducer?.data);
  const metaListRaw = field.list ? metaData?.[field.list] : null;

  const [getData] = useLazyQuery(field.query || DUMMY_QUERY, {
    client: field.client || liveClassClientService,
    fetchPolicy: "no-cache",
  });

  const convertToLabelValue = (items: any[]) => {
    if (!items || items.length === 0) return [];
    return items.map((item: any) => ({
      label: item[field.keyValue.label],
      value: item[field.keyValue.value],
    }));
  };

  const mergeSelectedOption = (list: any[]) => {
    const selected = selectedOptionsRef.current;
    if (!selected || selected.length === 0) return list;
    const selectedVals = new Set(selected.map((s) => String(s.value)));
    const merged = [...selected];
    list.forEach((o: any) => {
      if (!selectedVals.has(String(o.value))) {
        merged.push(o);
      }
    });
    return merged;
  };

  // Sync options from Redux metadata when field.list is provided
  useEffect(() => {
    if (!field.list) return;
    const mapped = convertToLabelValue(metaListRaw || []);
    setOptions(mapped);
    setInitialOptions(mapped);
    setFilteredOptions(mapped);
    setHasLoaded(true);
  }, [metaListRaw]);

  // Reset when dependent field changes
  useEffect(() => {
    if (!field.dependentOn) return;

    const prevValueStr = prevParentValueRef.current;
    prevParentValueRef.current = parentValueStr;

    if (field.strictDependency !== false && isParentEmpty) {
      // Parent cleared — reset everything
      setValue?.(field.id, []);
      setOptions([]);
      setInitialOptions([]);
      setFilteredOptions([]);
      selectedOptionsRef.current = [];
      setHasLoaded(false);
    } else if (prevValueStr !== undefined && prevValueStr !== parentValueStr) {
      // Parent changed to a new value — reset so API/metadata re-fetches
      if (field.clearOnDependencyChange !== false) {
        setValue?.(field.id, []);
        selectedOptionsRef.current = [];
        setInputValue("");
      }
      if (!field.list) {
        setOptions([]);
        setInitialOptions([]);
        setFilteredOptions([]);
        selectedOptionsRef.current = [];
        setHasLoaded(false);
      }
    }
  }, [parentValueStr]);

  // Clear local selected reference when value cleared
  useEffect(() => {
    const watchedValue = watch?.(field.id);
    if (!watchedValue || watchedValue.length === 0) {
      selectedOptionsRef.current = [];
      setInputValue("");
    } else if (Array.isArray(watchedValue) && hasLoaded) {
       // if value is updated from outside, try to retain in selectedOptionsRef
       const newSelected = watchedValue.map(val => {
         const existing = selectedOptionsRef.current.find(o => o.value === val);
         if (existing) return existing;
         const inOptions = options.find(o => o.value === val) || initialOptions.find(o => o.value === val);
         if (inOptions) return inOptions;
         return { label: val, value: val }; // fallback if missing
       });
       selectedOptionsRef.current = newSelected;
    }
  }, [watch?.(field.id)]);

  // Resolve labels for pre-filled values (e.g. edit mode) without waiting for the dropdown to be opened
  useEffect(() => {
    const initialValue = watch?.(field.id);
    if (!Array.isArray(initialValue) || initialValue.length === 0 || field.list || hasLoaded) return;
    if (!field.query || !getData) return;
    if (field.dependentOn && isParentEmpty) return;
    if (field.presetOptions) return;

    (async () => {
      try {
        setLoading(true);

        const res = await getData({
          variables: {
            input: {
              ...getDependentPayload(),
              ...(field.extraPayload || {}),
              limit: 100,
              offset: 0,
            },
          },
        });

        let records = res?.data?.[field.gqlKey]?.[field.dataKey] || [];

        if (field.formatData) {
          records = field.formatData(records);
        }

        const mappedResults = convertToLabelValue(records);
        selectedOptionsRef.current = initialValue.map((val: any) => {
          const found = mappedResults.find((o: any) => String(o.value) === String(val));
          return found || { label: val, value: val };
        });

        setOptions(mappedResults);
        setInitialOptions(mappedResults);
        setFilteredOptions(mergeSelectedOption(mappedResults));
        setHasLoaded(true);
      } catch (error) {
        console.error(`[${field.id}] Prefill resolve error:`, error);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInitialLoad = async () => {
    if (field.list) return;               // 🔥 data comes from Redux metadata, no API call
    if (!field.query || !getData) return; // 🔥 stop API if no query
    if (hasLoaded) return;

    try {
      setLoading(true);
      setFilteredOptions([]);

      const res = await getData({
        variables: {
          input: {
            ...getDependentPayload(),
            ...(field.extraPayload || {}),
            limit: INITIAL_DATA_LIMIT,
            offset: 0,
          },
        },
      });

      let records =
        res?.data?.[field.gqlKey]?.[field.dataKey] || [];

      if (field.formatData) {
        records = field.formatData(records);
      }

      const mappedResults = convertToLabelValue(records);

      setOptions(mappedResults);
      setInitialOptions(mappedResults);
      setFilteredOptions(mergeSelectedOption(mappedResults));
      setHasLoaded(true);
    } catch (error) {
      console.error(`[${field.id}] Initial load error:`, error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (event: any) => {
    event.stopPropagation();
    const value = event.target.value;
    setInputValue(value);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (value.length < 3) {
      setFilteredOptions(mergeSelectedOption(options));
      return;
    }

    debounceRef.current = setTimeout(async () => {
      if (!field.query || !getData) return;
      try {
        setLoading(true);

        const res = await getData({
          variables: {
            input: {
              ...(field.searchFieldKey
                ? { [field.searchFieldKey]: value }
                : {}),
              ...getDependentPayload(),
              ...(field.extraPayload || {}),
              limit: 30,
              offset: 0,
            },
          },
        });

        let records =
          res?.data?.[field.gqlKey]?.[field.dataKey] || [];

        if (field.formatData) {
          records = field.formatData(records);
        }

        const mappedResults = convertToLabelValue(records);
        setFilteredOptions(mappedResults);
      } catch (error) {
        console.error(`[${field.id}] Search error:`, error);
        setFilteredOptions([]);
      } finally {
        setLoading(false);
      }
    }, 600);
  };

  const handleClearSearch = () => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    setInputValue("");
    setFilteredOptions(mergeSelectedOption(options));
  };

  const hasOptions =
    filteredOptions && filteredOptions.length > 0;

  const singleFormError =
    errors && errors[field.id]?.message;

  return (
    <Grid item {...field.breakpoints} zeroMinWidth sx={{ minWidth: 0, ...field.breakpoints?.sx }}>
      <Controller
        name={field.id}
        control={control}
        defaultValue={[]}
        render={({ field: { onChange, value } }) => {
          const currentValue = Array.isArray(value) ? value : [];
          const isValueSelected = (val: any) =>
            currentValue.some((v: any) => String(v) === String(val));
          return (
          <>
            <FormControl
              sx={{ width: "100%", maxWidth: "100%" }}
              size="small"
              className={classes.border}
              error={!!singleFormError}
            >
              <InputLabel>{field.label}</InputLabel>

              <StyledSelect
                multiple
                value={currentValue}
                label={field.label}
                onOpen={handleInitialLoad}
                renderValue={(selected: any) => {
                  const selectedArr = Array.isArray(selected) ? selected : [];
                  if (selectedArr.length === 0) return "";

                  const names = selectedArr.map((val: any) => {
                    const found =
                      selectedOptionsRef.current.find((o: any) => String(o.value) === String(val)) ||
                      filteredOptions.find((o: any) => String(o.value) === String(val)) ||
                      initialOptions.find((o: any) => String(o.value) === String(val)) ||
                      options.find((o: any) => String(o.value) === String(val));
                    return found?.label || val;
                  });

                  const namesLength =
                    names.length > 1 ? ` +${names.length - 1}` : "";
                  const displayText = names
                    .join(", ")
                    .replace(/(.{15})..+/, `$1...${namesLength}`);

                  return (
                    <span
                      style={{
                        display: "block",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        maxWidth: "100%",
                      }}
                      title={names.join(", ")}
                    >
                      {displayText}
                    </span>
                  );
                }}
                onChange={(e: any) => {
                  const rawValue = e.target.value;
                  const newValue =
                    typeof rawValue === "string" ? rawValue.split(",") : rawValue;

                  if (newValue && newValue.includes(SELECT_ALL_VALUE)) {
                    const allValues = filteredOptions.map((o: any) => o.value);
                    const isAllSelected =
                      allValues.length > 0 &&
                      allValues.every((v: any) => isValueSelected(v));

                    if (isAllSelected) {
                      onChange([]);
                      selectedOptionsRef.current = [];
                    } else {
                      onChange(allValues);
                      selectedOptionsRef.current = mergeSelectedOption(filteredOptions);
                    }
                    return;
                  }

                  if (
                    !newValue ||
                    newValue.length === 0 ||
                    newValue.includes("")
                  ) {
                    onChange([]);
                    selectedOptionsRef.current = [];
                    setInputValue("");
                    setFilteredOptions(initialOptions);
                    return;
                  }

                  onChange(newValue);
                  const selected = filteredOptions.filter((o: any) =>
                    newValue.includes(o.value)
                  );
                  // Also include any previously selected options that might have been filtered out
                  const mergedSelected = [
                    ...selectedOptionsRef.current,
                    ...selected,
                  ]
                    .filter(
                      (v, i, a) =>
                        a.findIndex((t) => t.value === v.value) === i
                    )
                    .filter((v) => newValue.includes(v.value));
                  selectedOptionsRef.current = mergedSelected;
                }}
                onClose={() => {
                  if (debounceRef.current) {
                    clearTimeout(debounceRef.current);
                  }
                  setInputValue("");
                  setFilteredOptions(
                    mergeSelectedOption(initialOptions)
                  );
                }}
                disabled={
                  field.disabled ||
                  (field.dependentOn && field.strictDependency !== false && isParentEmpty)
                }
                MenuProps={{
                  sx: {
                    "&& .MuiList-root": {
                      display: "flex",
                      flexDirection: "column",
                      padding: "10px",
                      maxHeight: "300px",
                    },
                  },
                }}
              >
                <MenuItem disableRipple sx={{ py: 1, px: 0 }}>
                  <Box sx={{ width: "100%", px: 1 }}>
                    <TextField
                      label="Search here"
                      variant="outlined"
                      size="small"
                      fullWidth
                      value={inputValue}
                      onChange={handleSearchChange}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                      autoFocus
                      placeholder="Type to search..."
                      InputProps={{
                        endAdornment: inputValue && (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleClearSearch();
                            }}
                            sx={{ mr: -1 }}
                          >
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        ),
                      }}
                    />
                  </Box>
                </MenuItem>

                {loading ? (
                  <MenuItem disabled>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <CircularProgress size={16} />
                      <Typography variant="body2">
                        {inputValue.length >= 3
                          ? "Searching..."
                          : "Loading..."}
                      </Typography>
                    </Box>
                  </MenuItem>
                ) : !hasOptions ? (
                  <MenuItem disabled>
                    <Typography variant="body2">
                      No options found
                    </Typography>
                  </MenuItem>
                ) : null}

                {hasOptions && !field?.hideNone && (() => {
                  const allValues = filteredOptions.map((o: any) => o.value);
                  const isAllSelected =
                    allValues.length > 0 &&
                    allValues.every((v: any) => isValueSelected(v));

                  return (
                    <MenuItem value={SELECT_ALL_VALUE}>
                      <Checkbox checked={isAllSelected} />
                      <ListItemText primary="Select All" />
                    </MenuItem>
                  );
                })()}

                {filteredOptions.map((option: any) => (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                  >
                    <Checkbox checked={isValueSelected(option.value)} />
                    <ListItemText
                      primary={option.label}
                    />
                  </MenuItem>
                ))}
              </StyledSelect>
            </FormControl>

            {singleFormError && (
              <FieldError field={field} errors={errors} />
            )}
          </>
        )}}
      />
    </Grid>
  );
};

export default MultiSelectWithSearch;
